import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  RowSelectionOptions,
  GridApi,
  GridReadyEvent,
  CellEditingStoppedEvent,
  RowDragEndEvent,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  ICellEditorParams,
  CellFocusedEvent,
} from 'ag-grid-community'; // Column Definition Type Interface
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { CountyAttributes, PlotAttributes } from '~app/models';
import { CellEditorComponent } from '~app/components/shared/cell-editor/cell-editor.component';
import { PinnedCellRendererComponent } from '~app/components/shared/pinned-cell-renderer/pinned-cell-renderer.component';
import { CellRendererComponent } from '~app/components/shared/cell-renderer/cell-renderer.component';
import { LocationService, TripService } from '~app/services';
import { WaypointNameCellRenderer } from '~app/components/shared/cell-renderer/waypoint-name-cell-renderer.component';

import { RowOptionsComponent } from '../row-options/row-options.component';
import { SaveBtnComponent } from '~app/components/shared/cell-renderer/save-btn-renderer.component';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FormsUtility } from '~app/utilities';
import { NoRowsMessageComponent } from '~app/components/shared/no-rows-message.component';

@Component({
  selector: 'ciao-location-grid',
  imports: [AgGridAngular, FontAwesomeModule],
  templateUrl: './location-grid.component.html',
  styleUrl: './location-grid.component.less',
})
export class LocationGridComponent implements OnDestroy {
  constructor(
    private readonly tripService: TripService,
    private readonly locationService: LocationService,
    private readonly toastr: ToastrService
  ) {}
  @Input() rowData: PlotAttributes[];
  @Input() tripId: string;
  @Input() defaultColDef: ColDef;
  @Input() defaultRowSelection: RowSelectionOptions | 'single' | 'multiple';
  private _readOnly = false;
  /** When true (e.g. the trip is Closed) lock the grid: no cell editing, no
   * add-row, no row delete. The backend also rejects edits to closed trips. */
  @Input() set readOnly(value: boolean) {
    this._readOnly = !!value;
    this.applyReadOnly();
  }
  get readOnly(): boolean {
    return this._readOnly;
  }
  /** emit null if no errors.  emit _something_ (either array of errors, or just 'true') if there are errors */
  @Output() errors = new EventEmitter<ValidationErrors>();
  private subscriptions: Subscription = new Subscription();

  readonly countyComboOptions$ = this.locationService.allCounties$.pipe(
    map((people) =>
      people.map((county: CountyAttributes) => ({
        value: county,
        label:
          county.state.stateName !== county.countyName
            ? `${county.state.stateName} | ${county.countyName} | ${county.id}`
            : `${county.state.stateName}`,
      }))
    )
  );

  gridApi!: GridApi;
  // Input for new row
  inputRow: {} = {};
  pinnedTopRowData = [this.inputRow];
  allowSaveOnPinned = false;

  readonly testFormGroup = new FormGroup({
    id: new FormControl(''),
    description: new FormControl('', [
      FormsUtility.requiredNotEmpty,
      FormsUtility.isOverCharLimit,
      FormsUtility.minAlphanumericChars(2),
    ]),
    coords: new FormGroup(
      {
        lat: new FormControl<number>(null),
        lon: new FormControl<number>(null),
      },
      [
        (control) => {
          const { lat, lon } = control.value;
          if ((lat && !lon) || (lon && !lat)) {
            return {
              missingLatOrLong: 'Must have both Latitude and Longitude',
            };
          }
          return null;
        },
      ]
    ),
    countyCode: new FormControl<string>(null),
    plotNumber: new FormControl<string>(null, [Validators.maxLength(15)]),
  });

  locationColDefs: ColDef[] = [
    {
      field: 'id',
      hide: true,
    },
    {
      headerName: 'Location (at least one required)',
      field: 'description',
      flex: 2,
      minWidth: 250,
      editable: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: 'Location Name (*Required to save)',
        //Validators
        validators: [this.testFormGroup.controls.description.validator],
      },
      cellRendererSelector: (p: ICellRendererParams) => {
        if (p.node.rowPinned) {
          return { component: PinnedCellRendererComponent };
        } else {
          return { component: WaypointNameCellRenderer };
        }
      },
      cellRendererParams: {
        placeholder: 'Location Name (*Required to save)',
        validators: [this.testFormGroup.controls.description.validator],
      },
      equals: (a, b) => false,
      headerComponentParams: {
        template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
            <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order ag-hidden"></span>
            <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon ag-hidden"></span>
            <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon ag-hidden"></span>
            <span data-ref="eSortMixed" class="ag-header-icon ag-sort-mixed-icon ag-hidden"></span>
            <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon ag-hidden"></span>
            <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
            <p class="required">*</p>
            <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
    </div>`,
      },
      rowDrag: true,
      wrapText: true,
    },
    {
      headerName: 'Map Latitude',
      field: 'lat',
      minWidth: 150,
      editable: true,
      cellDataType: 'coords',
      valueGetter: (p) => {
        return p.data?.coords?.lat ?? null;
      },
      valueSetter: (p) => {
        p.data.coords = { ...p.data.coords, lat: p.newValue ?? null };
        return true;
      },
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'number',
        placeholder: 'ex. 39.5778',
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: 'ex. 39.5778',
      },
      headerComponentParams: {
        template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
            <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order ag-hidden"></span>
            <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon ag-hidden"></span>
            <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon ag-hidden"></span>
            <span data-ref="eSortMixed" class="ag-header-icon ag-sort-mixed-icon ag-hidden"></span>
            <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon ag-hidden"></span>
            <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
            <img src="assets/img/ag-grid/location.png" style="margin-left: 10px;"/>
            <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
    </div>`,
      },
    },
    {
      headerName: 'Map Longitude',
      field: 'lon',
      minWidth: 150,
      editable: true,
      valueGetter: (p) => {
        return p.data?.coords?.lon ?? null;
      },
      valueSetter: (p) => {
        p.data.coords = { ...p.data.coords, lon: p.newValue ?? null };
        return true;
      },
      cellDataType: 'coords',
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'number',
        placeholder: 'ex. -119.8524',
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: 'ex. -119.8524',
      },
      headerComponentParams: {
        template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
            <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order ag-hidden"></span>
            <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon ag-hidden"></span>
            <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon ag-hidden"></span>
            <span data-ref="eSortMixed" class="ag-header-icon ag-sort-mixed-icon ag-hidden"></span>
            <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon ag-hidden"></span>
            <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
           <img src="assets/img/ag-grid/location.png" style="margin-left: 10px;"/>
            <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
    </div>`,
      },
    },
    {
      headerName: 'State | County | County Code',
      field: 'county',
      minWidth: 200,
      editable: true,
      cellDataType: 'county',
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'combobox',
        options$: this.countyComboOptions$,
        placeholder: 'ex. Texas | Fannin | 48147',
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: 'ex. Texas | Fannin | 48147',
        fieldType: 'dropdown',
      },
    },
    {
      headerName: 'Plot Number',
      field: 'plotNumber',
      minWidth: 150,
      editable: true,
      cellDataType: 'text',
      wrapText: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: 'ex. 4528',
        disabled: false,
        validators: [this.testFormGroup.controls.plotNumber.validator],
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: 'ex. 4528',
      },
    },
    {
      headerName: ' ',
      field: 'rowOptions',
      pinned: 'right',
      editable: true,
      lockPinned: true,
      width: 50,
      sortable: false,
      equals: (a, b) => false,
      resizable: false,
      cellRendererSelector: (p: ICellRendererParams) => ({
        component: p.node?.rowPinned ? SaveBtnComponent : RowOptionsComponent,
      }),
      cellEditorSelector: (p: ICellEditorParams) => ({
        component: p.node?.rowPinned ? SaveBtnComponent : RowOptionsComponent,
      }),
      cellRendererParams: {
        showDelete: true,
        onDelete: (rowNode: IRowNode) => {
          let data = this.rowData.slice();
          data.splice(rowNode.rowIndex, 1);
          this.subscriptions.add(
            this.tripService
              .saveTrip({ id: this.tripId, locations: data })
              .pipe(
                tap((_) => {
                  this.toastr.success('Trip has been saved', 'Success');
                }),
                catchError((err) => {
                  this.toastr.error('Failed to Save Trip', 'Error');
                  return this.tripId;
                })
              )
              .subscribe()
          );
          return data;
        },
        onSaveClick: (rowNode: IRowNode) => {
          this.savePinnedRowData();
          return;
        },
        getRowCount: () => {
          return this.rowData?.length;
        },
        getSaveAbility: () => {
          return this.allowSaveOnPinned;
        },
        getRequiredFields: () => {
          return ['description'];
        },
      },
      cellEditorParams: {
        showDelete: true,
        onDelete: (rowNode: IRowNode) => {
          let data = this.rowData.slice();
          data.splice(rowNode.rowIndex, 1);
          this.subscriptions.add(
            this.tripService
              .saveTrip({ id: this.tripId, locations: data })
              .pipe(
                tap((_) => {
                  this.toastr.success('Trip has been saved', 'Success');
                }),
                catchError((err) => {
                  this.toastr.error('Failed to Save Trip', 'Error');
                  return this.tripId;
                })
              )
              .subscribe()
          );
          return data;
        },
        onSaveClick: (rowNode: IRowNode) => {
          this.savePinnedRowData();
          return;
        },
        getRowCount: () => {
          return this.rowData?.length;
        },
        getSaveAbility: () => {
          return this.allowSaveOnPinned;
        },
      },
      singleClickEdit: false, // important to allow click of button
    },
  ];

  gridOptions: GridOptions = {
    asyncTransactionWaitMillis: 500,
    columnDefs: this.locationColDefs,
    suppressRowHoverHighlight: true,
    noRowsOverlayComponent: NoRowsMessageComponent,
    noRowsOverlayComponentParams: {
      required: true,
      tableName: 'location',
    },
    dataTypeDefinitions: {
      county: {
        extendsDataType: 'object',
        baseDataType: 'object',
      },
      coords: {
        extendsDataType: 'object',
        baseDataType: 'object',
      },
    },
    rowDragManaged: true,
    //Allow tabbing to row options for accessability
    onCellKeyDown: (event: any) => {
      if (event.event.code === 'Enter' || event.event.code === 'NumpadEnter') {
        const isPinned = event.column.getPinned();
        if (isPinned) {
          // Prevent default and stop propagation to avoid double Enter key requirement
          event.event.preventDefault();
          event.event.stopPropagation();

          let cell = this.gridApi.getCellRendererInstances({
            rowNodes: [event.api.getRowNode(event.node?.id)],
            columns: [event.column],
          })[0];
          if (cell) {
            let renderer = cell as RowOptionsComponent;
            renderer.openMenu();
          }
        }
      }
    },
    getRowId: (params) => params.data.id,
    getRowHeight: (p) => (p.node.rowPinned == 'top' ? 50 : 40),
    onRowDragEnd: (event) => this.onRowDragEnd(event),
    onCellValueChanged: (params): any => {
      // This is after the cell changes and we attempt to save.
      let field = params.column.getColId();
      if (params.node?.rowPinned === 'top') {
        return;
      }
      if (field === 'rowOptions') {
        return;
      }

      console.log('Cell Value Changed');
      // Figure out if all rows are valid
      let valid = this.isTableValid();

      if (valid) {
        this.saveLocationData();
      } else {
      }
    },
  };

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.applyReadOnly();
  }

  /** Force a column non-editable and hide its row-delete action (if any). */
  private toReadOnlyColDef(col: ColDef): ColDef {
    const next: ColDef = { ...col, editable: false };
    for (const key of ['cellRendererParams', 'cellEditorParams'] as const) {
      const params = (next as any)[key];
      if (params && typeof params === 'object' && 'showDelete' in params) {
        (next as any)[key] = { ...params, showDelete: false };
      }
    }
    return next;
  }

  /** Apply (or clear) the closed-trip read-only lock on the grid. */
  private applyReadOnly() {
    if (!this.gridApi) return;
    this.gridApi.setGridOption(
      'columnDefs',
      this._readOnly
        ? this.locationColDefs.map((c) => this.toReadOnlyColDef(c))
        : this.locationColDefs
    );
    // Remove the "add new row" pinned input when read-only.
    this.pinnedTopRowData = this._readOnly ? [] : [this.inputRow];
    this.gridApi.setGridOption('pinnedTopRowData', this.pinnedTopRowData);
  }

  get gridRows() {
    let rows: IRowNode[] = [];
    this.gridApi.forEachNode((node) => {
      if (!node.rowPinned) {
        rows.push(node);
      }
    });
    return rows;
  }

  get finalResult(): PlotAttributes[] {
    return this.gridRows.map((node) => node.data as PlotAttributes);
  }

  onCellEditingStopped(params: CellEditingStoppedEvent) {
    if (params.rowPinned !== 'top') {
      return;
    }
    // if pinned row data is filed out then attempt to add it to the list
    if (this.isRowValid(params.node)) {
      // enable/disable the save button
      this.allowSaveOnPinned = true;
      this.gridApi.refreshCells({ force: true });
    } else {
      this.allowSaveOnPinned = false;
      this.gridApi.refreshCells({ force: true });
    }
    return;
  }

  onRowDragEnd(event: RowDragEndEvent) {
    this.subscriptions.add(
      this.tripService
        .saveTrip({ id: this.tripId, locations: this.finalResult })
        .pipe(
          tap(() => this.toastr.success('Trip has been saved', 'Success')),
          catchError((err) => {
            this.toastr.error('Failed to Save Trip', 'Error');
            console.error(err);
            // Return Real Trip on Error
            return this.tripId;
          })
        )
        .subscribe()
    );
  }

  onMapClick(coords: PlotAttributes['coords']) {
    this.inputRow = { coords };
    this.pinnedTopRowData = [this.inputRow];
    // const col = this.gridApi.getColumn('description');
    const pinnedRow = this.gridApi.getPinnedTopRow(0);
    this.gridApi.ensureNodeVisible(pinnedRow, 'top');
    this.gridApi.ensureColumnVisible('description');
    this.gridApi.setFocusedCell(0, 'description', 'top');
    this.gridApi.startEditingCell({
      colKey: 'description',
      rowPinned: 'top',
      rowIndex: 0,
    });
  }

  onKeyDown(event: any) {
    if (!this.allowSaveOnPinned) return;
    if (event.event.key === 'Enter' || event.event.code === 'NumpadEnter') {
      const focusedCell = this.gridApi.getFocusedCell();
      if (focusedCell.rowPinned) {
        this.savePinnedRowData();
      } else {
        this.saveLocationData();
      }
    }
    return;
  }

  // Needed for succesfull tabbing
  onCellFocused(_event: CellFocusedEvent) {
    // reset top pinned row allows immediate focus on row options for some reason
    this.pinnedTopRowData = [this.inputRow];
  }

  savePinnedRowData() {
    //get pinned row node
    var rowNode = this.gridApi.getPinnedTopRow(0);
    // add row
    this.gridApi.applyTransaction({ add: [rowNode?.data] });
    //reset pinned row
    this.inputRow = {};
    this.pinnedTopRowData = [this.inputRow];
    this.allowSaveOnPinned = false;
    this.saveLocationData();
  }

  saveLocationData() {
    this.gridApi.forEachNode((node) => {
      // only take in the non-pinned nodes
      if (node.rowPinned === 'top') {
        return;
      } else {
        let temp = this.convertDataToAttr(node.data);
        // add to/update rowData
        if (temp.id) {
          //existing row item so update it
          const index = this.rowData.findIndex((row) => row.id === temp.id);
          this.rowData[index] = temp;
        } else {
          //if not add it
          this.rowData.push(temp);
        }
      }
    });
    this.subscriptions.add(
      this.tripService
        .saveTrip({ id: this.tripId, locations: this.rowData })
        .pipe(
          tap(() => this.toastr.success('Successfully Saved Trip')),
          catchError((err) => {
            this.toastr.error('Failed to Save Trip');
            console.error(err);
            // Return Real Trip on Error
            return this.tripId;
          })
        )
        .subscribe()
    );
  }

  // take ag grid data and transform into saveable data
  convertDataToAttr(rowData) {
    if (!rowData?.coords?.lat && !rowData?.coords?.lon) {
      rowData.coords = null;
      // if just one is 0/undefined, then saving will throw an error, as it should.
      // not valid if one or the other is undefined.
    }
    let location = {
      id: rowData?.id ?? undefined,
      coords: rowData?.coords ?? null,
      countyCode: rowData?.county?.id ?? null,
      description: rowData?.description,
      plotNumber: rowData?.plotNumber ?? null,
      textCoords: rowData?.textCoords ?? null,
    };
    return location;
  }

  getCellRenderer(p: ICellRendererParams<any, any, any>) {
    const node = p.node;
    const pinnedRenderer = {
      component: PinnedCellRendererComponent,
    };
    const normalRenderer = {
      component: CellRendererComponent,
    };
    if (node.rowPinned === 'top') {
      return pinnedRenderer;
    } else {
      return normalRenderer;
    }
  }

  private isPinnedRowDataCompleted(): boolean {
    const pinnedData = this.gridApi.getPinnedTopRow(0)?.data;
    const descriptionFilled = pinnedData?.description?.trim()?.length > 0;
    return descriptionFilled;
  }

  /**
   *
   * @param row
   * @param property The specific property in question, such as 'description'.  May be nested.  Excluding this checks the whole row.
   * @returns
   */
  isRowValid(row: IRowNode<PlotAttributes>, property?: string) {
    this.testFormGroup.reset(row.data);
    if (property) {
      // if get(property) isn't there, then this should error because we missed something
      const validationProperty = this.testFormGroup.get(property);
      return validationProperty.valid;
    } else {
      for (const propertyKey in row.data) {
        if (Object.hasOwn(row.data, propertyKey)) {
          const validationProperty = this.testFormGroup.get(propertyKey);

          if (validationProperty && !validationProperty.valid) {
            return false;
          }
        }
      }
    }

    return this.testFormGroup.valid;
  }

  isTableValid() {
    if (!this.gridApi) {
      this.errors.emit(null);
      return true;
    }
    const errors = [];
    let everythingValid = true;

    this.gridApi.forEachNode((node) => {
      if (!this.isRowValid(node)) {
        everythingValid = false;
        errors.push(this.testFormGroup.errors);
      }
    });

    this.errors.emit(everythingValid ? null : errors);
    return everythingValid;
  }
}
